'use client';

import React from 'react';
import styled from 'styled-components';
import { focusRing } from '@liftango/ui-tokens';
import type { DefaultTheme } from 'styled-components';

export type BannerTone = 'info' | 'success' | 'warn' | 'error';

export interface BannerProps {
  tone?: BannerTone;
  icon?: React.ReactNode;
  title: React.ReactNode;
  body?: React.ReactNode;
  action?: React.ReactNode;
  onDismiss?: () => void;
  className?: string;
}

const toneSurface = (tone: BannerTone, theme: DefaultTheme) => {
  const map = {
    info: theme.colors.semantic.info,
    success: theme.colors.semantic.success,
    warn: theme.colors.semantic.warn,
    error: theme.colors.semantic.error,
  } as const;
  const sem = map[tone];
  return { bg: sem.soft, color: sem.color };
};

const Surface = styled.div<{ $tone: BannerTone }>`
  display: flex;
  align-items: center;
  gap: ${(p) => p.theme.space[3]};
  padding: ${(p) => p.theme.space[3]} ${(p) => p.theme.space[5]};
  background: ${(p) => toneSurface(p.$tone, p.theme).bg};
  color: ${(p) => p.theme.colors.neutral.ink};
  border-bottom: 1px solid ${(p) => toneSurface(p.$tone, p.theme).color}33;
`;

const IconSlot = styled.span<{ $tone: BannerTone }>`
  display: inline-flex;
  flex-shrink: 0;
  color: ${(p) => toneSurface(p.$tone, p.theme).color};
`;

const TextStack = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Title = styled.div`
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.body};
  font-weight: ${(p) => p.theme.fontWeight.semibold};
  line-height: 1.4;
`;

const Body = styled.div`
  margin-top: ${(p) => p.theme.space[0.5]};
  font-family: ${(p) => p.theme.fontFamily.ui};
  font-size: ${(p) => p.theme.fontSize.sm};
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  line-height: 1.55;
`;

const ActionSlot = styled.div`
  flex-shrink: 0;
`;

const DismissButton = styled.button`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: none;
  background: transparent;
  color: ${(p) => p.theme.colors.neutral.inkSoft};
  border-radius: ${(p) => p.theme.radius.sm};
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: ${(p) => p.theme.colors.neutral.ink};
  }
  ${focusRing}
`;

/**
 * Page-level full-width notification. Tinted background covering the
 * whole row, hairline border-bottom, optional icon/action/dismiss. Place
 * directly under the top bar or above a content region. For per-region
 * notifications use {@link Alert}.
 */
export function Banner({
  tone = 'info',
  icon,
  title,
  body,
  action,
  onDismiss,
  className,
}: BannerProps) {
  const role = tone === 'error' ? 'alert' : 'status';
  return (
    <Surface role={role} $tone={tone} className={className}>
      {icon !== undefined && <IconSlot $tone={tone}>{icon}</IconSlot>}
      <TextStack>
        <Title>{title}</Title>
        {body !== undefined && <Body>{body}</Body>}
      </TextStack>
      {action !== undefined && <ActionSlot>{action}</ActionSlot>}
      {onDismiss !== undefined && (
        <DismissButton type="button" onClick={onDismiss} aria-label="Dismiss">
          ×
        </DismissButton>
      )}
    </Surface>
  );
}
